import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import ProductList from './ProductList';
import Loader from './Loader';
import useFilters from '../hooks/useFilters';

const GET_SEARCH_RESULTS = gql`
    query getSearchResults($inputText: String!, $currentPage: Int, $pageSize: Int, $filters: ProductAttributeFilterInput, $sort: ProductAttributeSortInput) {
        products(search: $inputText, currentPage: $currentPage, pageSize: $pageSize, filter: $filters, sort: $sort) {
            aggregations {
                label
                count
                attribute_code
                options {
                    label
                    value
                    count
                }
                position
            }
            items {
                id
                uid
                sku
                name
                small_image {
                    url
                }
                url_key
                url_suffix
                price {
                    regularPrice {
                        amount {
                            value
                            currency
                        }
                    }
                }
                price_range {
                    minimum_price {
                        final_price {
                            currency
                            value
                        }
                        discount {
                            amount_off
                        }
                    }
                }
                rating_summary
                review_count
                stock_status
                __typename
            }
            page_info {
                current_page
                page_size
                total_pages
            }
            total_count
        }
    }
`;

const Search = () => {
    const location = useLocation();
    const { updateBreadcrumbs } = useBreadcrumb();
    const pageSize = 12;

    // Get search query from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const searchTerm = searchParams.get('q') || '';

    // Use filters hook (pass null for categoryId since this is search)
    const {
        currentPage,
        currentSort,
        activeFilters,
        hasActiveFilters,
        isLayerNavigationVisible,
        addFilter,
        removeFilter,
    clearAllFilters,
        setSort,
        setPage,
        hideLayerNavigation,
        showLayerNavigation,
        setPriceRange,
        graphqlFilters,
        graphqlSort
    } = useFilters(null, true);

    // Update breadcrumbs for search page
    useEffect(() => {
        if (searchTerm) {
            updateBreadcrumbs([
                { label: 'Home', path: '/' },
                { label: `Search: "${searchTerm}"`, path: location.pathname + location.search }
            ]);
        } else {
            updateBreadcrumbs([
                { label: 'Home', path: '/' },
                { label: 'Search', path: '/search' }
            ]);
        }
    }, [searchTerm, location.pathname, location.search, updateBreadcrumbs]);

    // Prepare variables for GraphQL query
    const queryVariables = useMemo(() => {
        const vars = {
            inputText: searchTerm,
            currentPage: currentPage,
            pageSize: pageSize
        };

        if (graphqlFilters && Object.keys(graphqlFilters).length > 0) {
            vars.filters = graphqlFilters;
        }

        if (graphqlSort) {
            vars.sort = graphqlSort;
        }

        return vars;
    }, [searchTerm, currentPage, graphqlFilters, graphqlSort]);

    const { data, loading, error } = useQuery(GET_SEARCH_RESULTS, {
        variables: queryVariables,
        skip: !searchTerm
    });

    const handlePageChange = (page) => {
        setPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!searchTerm) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h1>Search</h1>
                <p>Please enter a search term to find products.</p>
            </div>
        );
    }

    if (loading) {
        return <Loader fullScreen />;
    }

    if (error) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h1>Search Error</h1>
                <p>An error occurred while searching for "{searchTerm}": {error.message}</p>
            </div>
        );
    }

    const products = data?.products?.items || [];
    const totalCount = data?.products?.total_count || 0;
    const pageInfo = data?.products?.page_info || {};
    const totalPages = pageInfo.total_pages || 1;

    return (
        <div className="category-page">
            <div style={{ marginBottom: '1rem' }}>
                <h1 className="page-title">Search Results</h1>
                <p>
                    {totalCount > 0
                        ? `Found ${totalCount} products for "${searchTerm}"`
                        : `No products found for "${searchTerm}"`
                    }
                </p>
            </div>

            <ProductList
                products={products}
                totalCount={totalCount}
                pageInfo={pageInfo}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                currentSort={currentSort}
                onSortChange={setSort}
                enabledLayerNavigation={true}
                activeFilters={activeFilters}
                onAddFilter={addFilter}
                onRemoveFilter={removeFilter}
                onClearAllFilters={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                isLayerNavigationVisible={isLayerNavigationVisible}
                onHideLayerNavigation={hideLayerNavigation}
                onShowLayerNavigation={showLayerNavigation}
                onSetPriceRange={setPriceRange}
                categoryId={null}
                searchTerm={searchTerm}
            />
        </div>
    );
};

export default Search;